import React, {useEffect, useMemo, useState} from "react";
import {Breadcrumb, Layout, Tabs, Spin} from "antd";
import {useGetOrganisationQuery} from "../services/nestjs-base-api";
import {useAuth} from "../hooks/useAuth";
import {IOrganisation} from "../services/nestjs-base-api/types";
import OrganisationDetails from "../components/Organisation/OrganisationDetails";
import OrganisationUsers from "../components/Organisation/OrganisationUsers";
import OrganisationFeatures from "../components/Organisation/OrganisationFeatures";
import {FormattedMessage, useIntl} from "react-intl";
import {Link, useLocation} from "react-router-dom";
import { PlanLevelEnum } from "../enums/PlanLevelEnum";
import { AppstoreOutlined, TeamOutlined, RocketOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Content } = Layout;

interface IProps {
}

interface LocationState {
    activeTab?: string;
}

const StyledContent = styled(Content)`
  margin: 0 24px;
  
  @media (max-width: 768px) {
    margin: 0 16px;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin: 24px 0 16px 0;
  
  .ant-breadcrumb-link {
    color: #666;
    
    &:hover {
      color: #4169e1;
    }
  }
  
  a {
    color: #4169e1;
    text-decoration: none;
    
    &:hover {
      color: #1890ff;
    }
  }
`;

const ContentWrapper = styled.div`
  padding: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 24px 0;
  color: #1a2847;
  font-size: 28px;
  font-weight: 600;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    
    .ant-tabs-tab {
      font-size: 15px;
      padding: 12px 16px;
      
      .anticon {
        margin-right: 8px;
        font-size: 16px;
      }
      
      &:hover {
        color: #4169e1;
      }
      
      &.ant-tabs-tab-active {
        color: #4169e1;
        
        .ant-tabs-tab-btn {
          color: #4169e1;
        }
      }
    }
  }
  
  .ant-tabs-content-holder {
    background: #fafafa;
    border-radius: 8px;
    padding: 24px;
    
    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`;

const LoadingWrapper = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const OrganisationPage: React.FunctionComponent<IProps> = (props) => {
    const intl = useIntl();
    const { user, logout } = useAuth();
    const location = useLocation();

    // Get active tab from location state if available
    const { activeTab } = (location.state as LocationState) || {};
    const [currentTab, setCurrentTab] = useState(activeTab || 'details');

    const {data, error, isLoading} = useGetOrganisationQuery({jwtToken: user.jwtToken}, {pollingInterval: 600000});

    useEffect(() => {
        if (error) {
            if ('status' in error) {
                if (error.status === 401) {
                    logout();
                }
            }
        }
    }, [error, logout]);

    // Update active tab when location state changes
    useEffect(() => {
        if (activeTab) {
            setCurrentTab(activeTab);
        }
    }, [activeTab]);

    const organisation = useMemo(() => {
        console.log('Organisation data:', data);
        console.log('User organisation data:', user.organisation);
        
        // If data is not yet available, create a default organisation with user.organisation data
        if (!data && user.organisation) {
            // Ensure we have a plan value
            const defaultOrg = {
                id: user.organisation.id || 0,
                name: user.organisation.name || 'Your Organisation',
                plan: user.organisation.plan || PlanLevelEnum.Plan1
            } as IOrganisation;
            
            console.log('Created default organisation:', defaultOrg);
            return defaultOrg;
        }
        
        const result = data ? data as unknown as IOrganisation : undefined;
        console.log('Final organisation object:', result);
        return result;
    }, [data, user.organisation]);

    const detailsTab = organisation ? <OrganisationDetails organisation={organisation} /> : (
        <LoadingWrapper>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#666' }}>
                {intl.formatMessage({ id: 'organisation.loading' }) || 'Loading organisation details...'}
            </p>
        </LoadingWrapper>
    );

    const usersTab = <OrganisationUsers />;
    const featuresTab = <OrganisationFeatures />;

    const tabs = [
        {
            label: (
                <span>
                    <AppstoreOutlined />
                    {intl.formatMessage({ id: 'organisation.tabs.details' }) || 'Details'}
                </span>
            ),
            key: 'details',
            children: detailsTab,
        }, 
        {
            label: (
                <span>
                    <TeamOutlined />
                    {intl.formatMessage({ id: 'organisation.tabs.users' }) || 'Users'}
                </span>
            ),
            key: 'users',
            children: usersTab,
        },
        {
            label: (
                <span>
                    <RocketOutlined />
                    {intl.formatMessage({ id: 'organisation.tabs.features' }) || 'Features'}
                </span>
            ),
            key: 'features',
            children: featuresTab,
        },
    ];

    return (
        <StyledContent>
            <StyledBreadcrumb>
                <Breadcrumb.Item>{user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
                <Breadcrumb.Item><Link to="/dashboard/events"><FormattedMessage id={'section.eventsName'} /></Link></Breadcrumb.Item>
                <Breadcrumb.Item><FormattedMessage id={'section.organisationName'} /></Breadcrumb.Item>
            </StyledBreadcrumb>
            <ContentWrapper>
                <PageTitle>{organisation?.name}</PageTitle>
                <StyledTabs
                    activeKey={currentTab}
                    onChange={setCurrentTab}
                    type="card"
                    size="large"
                    items={tabs}
                />
            </ContentWrapper>
        </StyledContent>
    );
}

export default OrganisationPage;